import { useEffect, useRef } from 'react';
import { resolveHotkeyAction, isTypingTarget, DEFAULT_HOTKEYS, type HotkeyAction } from './hotkeys.ts';
import { targetOwnsKey, isControlInput } from './targetOwnsKey.ts';

export type HotkeyHandlers = Partial<Record<HotkeyAction, () => void>>;

/** MVP-500: the default for `locked`, at module scope and frozen, so a caller that passes nothing
 * hands the same Set identity on every render rather than a fresh empty one each time. */
const EMPTY_LOCK: ReadonlySet<HotkeyAction> = Object.freeze(new Set<HotkeyAction>()) as ReadonlySet<HotkeyAction>;

/**
 * DOM glue for AC-OPS-5 — attaches `hotkeys.ts`'s pure `resolveHotkeyAction` to this WINDOW's own
 * `keydown` (per SPEC's decision note: "global hotkeys work only while a mv-catalog-player window
 * has focus", i.e. no OS-level global registration). Kept deliberately dumb: callers pass a map of
 * only the actions they can currently handle — a missing entry is a silent no-op, never a crash,
 * so a screen that has nothing to search or nothing to undo just omits that key. "Silent" is
 * literal as of MVP-495: the browser's own default for that key is cancelled too, so an omitted
 * action does nothing at all rather than scrolling the view (see `onKeyDown` below).
 *
 * Refs (not the raw params) back the listener closure so a re-render that only changes which
 * handlers are bound never has to tear down and reattach the `keydown` listener itself.
 */
export function useHotkeys(
  handlers: HotkeyHandlers,
  bindings: Record<HotkeyAction, string> = DEFAULT_HOTKEYS,
  enabled = true,
  locked: ReadonlySet<HotkeyAction> = EMPTY_LOCK
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;
  // Ref-backed like the two above, and for the same reason: the mode being switched on mid-set
  // changes which actions are refused, not which window owns the `keydown`.
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      // MVP-555: two gates, and their ORDER is the whole fix — read this before changing either.
      //
      // `targetOwnsKey` runs FIRST and answers per KEY: the focused element already does something
      // with this one key, so the table steps aside. Space on a focused button presses that button;
      // `n` on it still skips the song.
      //
      // `isTypingTarget` runs SECOND and answers per ELEMENT: everything dies while someone types.
      // But it only sees `tagName`, so it says INPUT — and therefore "typing" — for `<input
      // type="checkbox" role="switch">`, which is the pause control of every rotation slot. That is
      // why it is NARROWED by `isControlInput`: a checkbox/radio/button-typed input is a control,
      // not a field, so it yields Space (via the first gate) and hands every other key to the table.
      // A real `<input type="text">` is not a control input, so it still swallows everything.
      //
      // Swapping the two gates does NOT work: `isTypingTarget` would return early for the switch
      // before `targetOwnsKey` was ever asked, and the switch would go on eating `n` and `s`.
      if (targetOwnsKey(target, event)) return;
      if (isTypingTarget(target) && !isControlInput(target)) return;
      const action = resolveHotkeyAction(event, bindingsRef.current);
      if (!action) return;
      // MVP-495: the default is swallowed as soon as the ACTION resolved, BEFORE the handler
      // lookup. The binding table is the app's claim on that key, and a claimed key must not do
      // two different things depending on which screen is up: with the `preventDefault()` below
      // the lookup, a screen that passes a partial handler map handed the key straight back to the
      // browser — ↑/↓ and Space scrolled the Cockpit out from under the operator in a dark room.
      // The key an action did NOT resolve for is untouched (`if (!action) return` above): the app
      // eats only what it claims.
      event.preventDefault();
      // MVP-500: the storefront refusal, and it belongs HERE — after the default is cancelled,
      // before the handler is looked up. Claim the key, kill it, then refuse. Swapping these two
      // lines undoes MVP-495 above for exactly the keys that most need it: a refused `Ctrl+Z` would
      // reach the browser and every other `keydown` listener on the page instead of dying here,
      // which is "unhandled", not "locked". There is deliberately no toast and no sound — someone
      // leaning on the keyboard would produce a storm of them, and `StorefrontModeBar` already
      // states the rule permanently, once, on screen. See STOREFRONT_LOCKED_ACTIONS in hotkeys.ts.
      if (lockedRef.current.has(action)) return;
      const handler = handlersRef.current[action];
      if (!handler) return;
      handler();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
